import React, { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik"
// import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useTheme } from '~/ui/styled'
import { Flex, ColumnFlex, StartFlex, CenterFlex, BetweenFlex, Text, Button, GradientButton, IconFont } from "~components/El"
import { TextField, MultiLineTextField } from "~components/InputField";
import { Tooltip } from "~components/Layout";
import { RedPacketSchema } from "~schema"
import { RedPacketType } from "~proto";
import { useStores } from "~models"
import { usePopup } from "~hooks/usePopup"
import useGroupActions from "~hooks/useGroupActions"
import { withPopupScrollable } from "~hoc/withPopupScrollable"
import { Theme } from "~theme/styled";
import { globalToast } from "~utils";
import QuickButtons from "../components/QuickButtons";
import TypeTabs from "../components/TypeTabs";
import MemberListView from "../components/MemberListView";
import MemberItemContent from "../components/MemberItemContent";


const types = [
    { value: RedPacketType.LUCK, label: "拼手气红包" },
    { value: RedPacketType.NORMAL, label: "普通红包" },
    { value: RedPacketType.EXCLUSIVE, label: "专属红包" },
]

const LabelContent: React.FC<{ theme: Theme; label: string; content: string, count: string}> = ({ theme, label = "", content = "", count = "0" }) => {
    return (
        <BetweenFlex width="100%" minWidth={0}>
            <StartFlex width="auto" minWidth={0} flexShrink={1}>
                <Text fontSize="14px" color={theme.colors.text} fontWeight={600} mr="4px">{label}</Text>
                <Tooltip content={content}>
                  <IconFont name="xttz" color={theme.colors.secondary} size={14} />
                </Tooltip>
            </StartFlex>
            <Text flexShrink={0} fontSize="12px" color={theme.colors.secondary} ml="8px">可用余额：¥{count}</Text>
        </BetweenFlex>
    )
}
const PrefixContent: React.FC<{ theme: Theme; text: string; }> = ({ theme, text = "" }) => (
    <GradientButton
        width={16}
        height={16}
        colors={['#fb994d', '#ee4b49']}
        borderRadius={8}
        style={{ marginLeft: 12 }}
        onClick={() => { } } >
        <Text fontSize="14px" color={theme.colors.textLight}>{text}</Text>
    </GradientButton>
)

interface RedPacketParams {
    chat_id: number
    chat_cid: string
    total_amount: number | string
    total_count?: number | string
    redpacket_type?: RedPacketType
    target_user_id?: number | string
    blessing_words?: string
    wp_password?: string
}

/** 按 chat_id 取草稿键（与 Chat.identifier 一致） */
function gameGroupDraftKey(chat: { chat_id?: string }): string | null {
    const cid = chat?.chat_id
    return cid != null && cid !== "" ? String(cid) : null
}

const RedPacketForm: React.FC<{ chat?: any; channelChatInfo?: any; popupId?: number; onSuccess?: () => void }> = ({ chat: chatProp, channelChatInfo, popupId, onSuccess }) => {
    const theme = useTheme()
    const popup = usePopup();
    const { chatStore, walletStore } = useStores()
    const { onChatRedPacket } = useGroupActions();
    const [loading, setLoading] = useState(false)
    const current = chatProp ?? chatStore.current ?? {}
    const chatTypeEnum = current?.chatTypeEnum
    const receiver = current?.receiver
    const isGameGroup = current?.isGameGroup

    const draftKey = isGameGroup ? gameGroupDraftKey(current) : null
    const savedDraft = draftKey ? chatStore.getGameGroupRedPacketDraft(draftKey) : undefined

    const initialValues = useMemo(() => {
        const luckDraft =
            savedDraft && savedDraft.type === RedPacketType.LUCK ? savedDraft : undefined
        return {
            type: RedPacketType.LUCK,
            amount: (luckDraft?.amount as string | null | undefined) ?? null,
            count: (luckDraft?.count as string | null | undefined) ?? null,
            remark: (luckDraft?.remark as string | undefined) ?? "",
            member: (luckDraft?.member as any) ?? null,
            chatTypeEnum,
            wp_password: "",
        }
    }, [chatTypeEnum, current.chat_id, popupId, savedDraft])

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: RedPacketSchema(channelChatInfo?.redpacket_max_amount),
        onSubmit: (values) => {
            if (loading) return
            if (chatTypeEnum === "PRIVATE" && !current.id) {
                globalToast.show("请先发送一条文本消息确认身份")
                return
            }
            const needPayPassword = walletStore.wp_confirmed && !walletStore.redPacketFormPayPwdVerifiedSession
            if (needPayPassword && !values.wp_password) {
                globalToast.show("请输入支付密码")
                return
            }
            setLoading(true)
            const params: RedPacketParams = {
                chat_id: current.id,
                chat_cid: current.chat_id,
                total_amount: values.amount,
                blessing_words: values.remark || "恭喜发财，大吉大利",
            }
            if (chatTypeEnum === "PRIVATE") {
                params.total_count = 1
                params.redpacket_type = RedPacketType.PRIVATE
                params.target_user_id = current?.receiver?.id || null
            }
            if (chatTypeEnum === "GROUP") {
                if (values.type === RedPacketType.EXCLUSIVE) {
                    params.total_count = 1
                } else {
                    params.total_count = values.count　? Number(values.count) : 1
                }
                if (values.member) {
                    params.target_user_id = values.member?.user_id || null
                }
                params.redpacket_type = values.type
            }
            if (walletStore.wp_confirmed && !walletStore.redPacketFormPayPwdVerifiedSession) {
                params.wp_password = values.wp_password
            }
            onChatRedPacket(params).then((data) => {
                if (walletStore.wp_confirmed && !walletStore.redPacketFormPayPwdVerifiedSession) {
                    walletStore.markRedPacketFormPayPwdVerified()
                    setFieldValue("wp_password", "")
                }
                const persistKey = isGameGroup ? gameGroupDraftKey(current) : null
                if (persistKey && values.type === RedPacketType.LUCK) {
                    chatStore.setGameGroupRedPacketDraft(persistKey, {
                        type: RedPacketType.LUCK,
                        amount: values.amount,
                        count: values.count,
                        remark: values.remark,
                        member: null,
                    })
                }
                setLoading(false)
                // 发送成功后通知外部收起底部操作区
                onSuccess?.()
                // popup?.close()
            }).catch((error) => {
                setLoading(false)
                if (error?.message && error?.message.includes("输入资金密码")) {
                    walletStore.resetRedPacketFormPayPwdSession()
                    setFieldValue("wp_password", "")
                }
                
                globalToast.show(error?.message || "发送失败")
            }).finally(() => {
                // 刷新获取账号余额的接口
                walletStore.getWalletInfo()
                popup?.close()
            })
        },
    });
    const { values, errors, touched, setFieldValue, handleSubmit } = formik;
    
    const typeDesc = useMemo(() => {
        if (values.type === RedPacketType.NORMAL) {
            return "平均分配给每个人";
        } else if (values.type === RedPacketType.EXCLUSIVE) {
            return "指定群成员领取";
        }
        return "金额随机分配，手气最佳者获得最多";
    }, [values.type]);

    const amountLabelContent = useMemo(() => {
        if (chatTypeEnum === "GROUP" && values.type === RedPacketType.LUCK) {
            return `群红包总金额不超过${channelChatInfo?.redpacket_max_amount || 10000}元`;
        } else if (chatTypeEnum === "GROUP" && values.type === RedPacketType.NORMAL) {
            return `群红包总金额不超过${channelChatInfo?.redpacket_max_amount || 10000}元`;
        } else {
            return `红包金额不超过${channelChatInfo?.redpacket_max_amount || 10000}元`;
        }
    }, [chatTypeEnum, values.type]);

    const quickButtonsOptions = useMemo(() => {
        if (chatTypeEnum === "GROUP" && values.type === RedPacketType.LUCK) {
            return ["10", "50", "100", "200", "500", "1000", "2000", "3000"];
        } else if (chatTypeEnum === "GROUP" && values.type === RedPacketType.NORMAL) {
            return ["10", "50", "100", "200"];
        } else {
            return ["10", "50", "100", "200"];
        }
    }, [chatTypeEnum, values.type]);

    const quickRemarkOptions = useMemo(() => {
        if (isGameGroup && values.type === RedPacketType.LUCK) {
            return ["龙", "虎", "龙大", "龙小", "虎大", "虎小"];
        }
        return []
    }, [isGameGroup, values.type]);

    const onMemberChange = useCallback((member) => {
        setFieldValue("member", member);
    }, [setFieldValue])

    const handleOpenContact = useCallback(async() => {
        const p = popup.open({
            stack: true,
            content: <MemberListView type="redPacket" onMemberChange={(member) => {
                onMemberChange(member)
                popup.close(p.id)
            }} onClose={() => popup.close(p.id)} />,
            type: "center"
        });
    }, [popup, onMemberChange]);

    return (
        <ColumnFlex
            width="100%"
            height={'auto'}
            padding="12px"
        >
            <ColumnFlex height={'auto'}  width="100%">
                <GradientButton
                    width={34}
                    height={34}
                    colors={['#fb994d', '#ee4b49']}
                    borderRadius={17}
                    onClick={() => { } } >
                    <IconFont name="hb1" color={theme.colors.textLight} size={16} />
                </GradientButton>
                <Flex height="auto" flexDirection="column" ml="10px">
                    <Text fontSize="16px" color={theme.colors.text} fontWeight={600}>{chatTypeEnum === "GROUP" ? "群红包" : "发红包"}</Text>
                    <Text fontSize="12px" color={theme.colors.secondary}>{chatTypeEnum === "GROUP" ? "发到" : "给"} {(chatTypeEnum === "GROUP" ? current?.title : receiver?.name) || ""}</Text>
                </Flex>
            </ColumnFlex>
            <Flex flexDirection="column" width="100%" mt="10px">
                {chatTypeEnum === "GROUP" && (
                    <>
                        <BetweenFlex width="100%" height="auto" padding="12px 0">
                            <Text fontSize="14px" color={theme.colors.text} fontWeight={600}>红包类型</Text>
                            <Text fontSize="12px" color={theme.colors.error}>{typeDesc}</Text>
                        </BetweenFlex>
                        <TypeTabs
                            value={values.type}
                            options={types}
                            onChange={(v) => setFieldValue("type", v)}
                        />
                    </>
                )}
                {values.type === RedPacketType.EXCLUSIVE && (
                    <>
                        <BetweenFlex width="100%" padding="12px 0">
                            <Text fontSize="14px" color={theme.colors.text} fontWeight={600}>指定领取人</Text>
                        </BetweenFlex>
                        {values.member && ( <MemberItemContent member={values.member} onClick={() => {
                            setFieldValue("member", null);
                        }} /> )}
                        {!values.member && ( 
                            <Button
                                flexDirection="column"
                                width="100%"
                                height="68px"
                                borderRadius="8px"
                                border={`1px solid ${theme.colors.border}`}
                                bg={theme.colors.background}
                                onClick={handleOpenContact}
                            >
                                <CenterFlex
                                    width="24px"
                                    height="24px"
                                    borderRadius="12px"
                                    border={`1px solid ${theme.colors.secondary}`}
                                >
                                    <IconFont name="nc" color={theme.colors.secondary} size={14}/>
                                </CenterFlex>
                                <Text fontSize="14px" color={theme.colors.secondary} mt="5px">点击选择群成员</Text>
                            </Button>
                        )}
                    </>
                )}
                
                
                <TextField
                    label={<LabelContent
                        theme={theme}
                        label={chatTypeEnum === "GROUP" ? (values.type === RedPacketType.LUCK ? "总金额" : (values.type === RedPacketType.NORMAL ? "单个金额" : "金额")) : "红包金额"}
                        content={amountLabelContent}
                        count={walletStore?.balance}
                        />
                    }
                    prefix={<PrefixContent theme={theme} text="¥" />}
                    value={values.amount}
                    placeholder="请输入金额"
                    keyboardType="decimal-pad"
                    onChangeText={(value) => setFieldValue("amount", value)}
                    error={touched.amount && typeof errors.amount === "string" ? errors.amount : ""}
                />
                <QuickButtons
                    value={values.amount}
                    options={quickButtonsOptions}
                    onChange={(v) => setFieldValue("amount", v)}
                />
                {chatTypeEnum === "GROUP" && values.type !== RedPacketType.EXCLUSIVE && (
                    <TextField
                        label={<LabelContent theme={theme} label="红包个数" content="红包个数1-100个" count={walletStore?.balance} />}
                        prefix={<PrefixContent theme={theme} text="个" />}
                        value={values.count}
                        placeholder="请输入红包个数"
                        keyboardType="number-pad"
                        onChangeText={(value) => setFieldValue("count", value)}
                        error={touched.count && typeof errors.count === "string" ? errors.count : ""}
                    />
                )}

                {walletStore.wp_confirmed && !walletStore.redPacketFormPayPwdVerifiedSession && (
                    <TextField
                        label="支付密码"
                        value={values.wp_password}
                        placeholder="请输入支付密码"
                        keyboardType="numeric"
                        maxLength={6}
                        secureTextEntry
                        required
                        onChangeText={(v) => setFieldValue("wp_password", v)}
                        error={touched.wp_password && errors.wp_password}
                    />
                )}
                
                <Flex flexDirection="column" width="100%" minWidth={0} mt="10px">
                    <MultiLineTextField
                        value={values.remark}
                        placeholder="恭喜发财，大吉大利"
                        maxLength={50}
                        numberOfLines={4}
                        style={{ height: 80 }}
                        onChangeText={(v) => setFieldValue("remark", v)}
                        error={touched.remark && errors.remark}
                    />
                    <QuickButtons
                        value={values.remark}
                        options={quickRemarkOptions}
                        span={6}
                        unit=""
                        onChange={(v) => setFieldValue("remark", v)}
                    />
                </Flex>
                
            </Flex>
            <BetweenFlex width="100%" padding="12px 0">
                <Button
                    width="48%"
                    height="44px"
                    borderRadius={theme.radii.lg}
                    bg={theme.colors.background}
                    onClick={() => {
                        popup?.close()
                    }}
                >
                    <Text color={theme.colors.infoText} fontSize={16}>取消</Text>
                </Button>
                <GradientButton
                    width="48%"
                    height={44}
                    colors={['#fb994d', '#ee4b49']}
                    borderRadius={8}
                    loading={loading}
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    <Text color={theme.colors.textLight} fontSize={16}>塞钱进红包</Text>
                </GradientButton>
            </BetweenFlex>
        </ColumnFlex>
    )
}

export default observer(withPopupScrollable(RedPacketForm, { mode: "bottom" }))
